import * as React from "react";
import { useParams } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import Title from "../Common/Title";
import BasicBreadcrumbs from "../Common/Breadcrums";
import {
  getVendorByVendorID,
  getVendorWishlist,
  removeVendorWishlistItem,
  addVendorWishlistItem,
  getProductById,
} from "../../Utils/vendor-customer-service";
import { formatDate } from "../../Utils/util-service";
import { extractIdFromGid } from "../../Utils/constant";
import SearchAutoComplete from "../ProductSearch/SearchAutoComplete";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fab,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

const PAGE_SIZE = 20;

const mergeWishlistRows = (rows = [], nodes = []) => {
  const productById = {};
  nodes.forEach((node) => {
    if (node?.id) {
      productById[node.id] = node;
    }
  });

  return rows.map((row) => ({
    ...row,
    product: productById[row.shopifyProductId] || null,
  }));
};

export default function VendorWishlist() {
  const { vendorID } = useParams();
  const [vendor, setVendor] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [lightbox, setLightbox] = React.useState({
    open: false,
    src: "",
    title: "",
  });
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [selectedSearchProduct, setSelectedSearchProduct] =
    React.useState(null);
  const [previewProduct, setPreviewProduct] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState("");
  const [adding, setAdding] = React.useState(false);

  const openLightbox = (src, title) => {
    if (!src) {
      return;
    }
    setLightbox({ open: true, src, title: title || "Wishlist product" });
  };

  const closeLightbox = () => {
    setLightbox({ open: false, src: "", title: "" });
  };

  const openDeleteConfirm = (item) => {
    setDeleteTarget(item);
  };

  const closeDeleteConfirm = () => {
    if (deleting) {
      return;
    }
    setDeleteTarget(null);
  };

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const resetAddDialogState = () => {
    setSelectedSearchProduct(null);
    setPreviewProduct(null);
    setPreviewLoading(false);
    setPreviewError("");
    setAdding(false);
  };

  const openAddDialog = () => {
    resetAddDialogState();
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    if (adding) {
      return;
    }
    setAddDialogOpen(false);
    resetAddDialogState();
  };

  const handleProductSearchSelect = (product) => {
    if (!product?.id || adding) {
      return;
    }

    setSelectedSearchProduct(product);
    setPreviewProduct(null);
    setPreviewError("");
    setPreviewLoading(true);

    const numericId = extractIdFromGid(product.id);
    getProductById(numericId)
      .then((response) => {
        if (response?.status === 200 && response?.data?.product) {
          setPreviewProduct(response.data.product);
          return;
        }
        setPreviewError("Unable to load product details.");
      })
      .catch(() => {
        setPreviewError("Unable to load product details.");
      })
      .finally(() => {
        setPreviewLoading(false);
      });
  };

  const handleAddToWishlist = () => {
    const shopifyProductId =
      previewProduct?.id || selectedSearchProduct?.id;
    const productName =
      previewProduct?.title ||
      selectedSearchProduct?.title ||
      "Product";

    if (!vendorID || !shopifyProductId) {
      showToast("Select a product before adding.", "error");
      return;
    }

    setAdding(true);
    addVendorWishlistItem(vendorID, {
      shopifyProductId,
      source: "ADMIN",
    })
      .then((response) => {
        if (response?.status === 201 || response?.status === 200) {
          if (response?.data?.created) {
            showToast(`${productName} added to wishlist`);
          } else if (response?.data?.added) {
            showToast(`${productName} is already in wishlist`, "info");
          } else {
            showToast(`${productName} added to wishlist`);
          }

          setAddDialogOpen(false);
          resetAddDialogState();
          setInitialLoading(true);
          setItems([]);
          setOffset(0);
          setHasMore(true);
          fetchWishlistPage(0, true);
          return;
        }

        const apiMessage =
          response?.response?.data?.message ||
          response?.message ||
          "Unable to add product to wishlist.";
        showToast(apiMessage, "error");
      })
      .catch(() => {
        showToast("Unable to add product to wishlist.", "error");
      })
      .finally(() => {
        setAdding(false);
      });
  };

  const fetchWishlistPage = React.useCallback(
    (nextOffset, replace = false) => {
      return getVendorWishlist(vendorID, PAGE_SIZE, nextOffset)
        .then((response) => {
          if (response?.status !== 200) {
            setError("Unable to load wishlist.");
            setHasMore(false);
            return;
          }

          const count = response?.data?.count || 0;
          const rows = response?.data?.rows || [];
          const nodes = response?.data?.data?.nodes || [];
          const merged = mergeWishlistRows(rows, nodes);

          setTotalCount(count);
          setItems((prev) => (replace ? merged : [...prev, ...merged]));
          setOffset(nextOffset + rows.length);
          setHasMore(nextOffset + rows.length < count);
        })
        .catch(() => {
          setError("Unable to load wishlist.");
          setHasMore(false);
        })
        .finally(() => {
          setInitialLoading(false);
        });
    },
    [vendorID],
  );

  React.useEffect(() => {
    if (!vendorID) {
      return;
    }

    getVendorByVendorID(vendorID).then((res) => {
      if (res?.data) {
        setVendor(res.data);
      }
    });

    setItems([]);
    setOffset(0);
    setHasMore(true);
    setInitialLoading(true);
    setError("");
    fetchWishlistPage(0, true);
  }, [vendorID, fetchWishlistPage]);

  const loadMore = () => {
    if (!hasMore) {
      return;
    }
    fetchWishlistPage(offset, false);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget || !vendorID) {
      return;
    }

    const productName = deleteTarget?.product?.title || "Product unavailable";
    const shopifyProductId = deleteTarget.shopifyProductId;

    setDeleting(true);
    removeVendorWishlistItem(vendorID, shopifyProductId)
      .then((response) => {
        if (response?.status === 200 && response?.data?.removed) {
          setItems((prev) =>
            prev.filter((item) => item.shopifyProductId !== shopifyProductId),
          );
          setTotalCount((prev) => Math.max(0, prev - 1));
          setOffset((prev) => Math.max(0, prev - 1));
          setDeleteTarget(null);
          showToast(`${productName} deleted`);
          return;
        }
        showToast("Unable to delete wishlist item.", "error");
      })
      .catch(() => {
        showToast("Unable to delete wishlist item.", "error");
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  const deleteProductName =
    deleteTarget?.product?.title || "Product unavailable";

  const previewImage =
    previewProduct?.images?.nodes?.[0]?.url ||
    previewProduct?.variants?.nodes?.[0]?.image?.url;
  const previewMinPrice =
    previewProduct?.priceRange?.minVariantPrice?.amount || "--";
  const previewMaxPrice =
    previewProduct?.priceRange?.maxVariantPrice?.amount || "--";

  return (
    <React.Fragment>
      <BasicBreadcrumbs children={vendor?.name} />
      <Title>Vendor Wishlist</Title>
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant="body1">
          {vendor?.name
            ? `Wishlist for ${vendor.name} (${vendor.nickName})`
            : "Loading vendor..."}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {totalCount} item{totalCount === 1 ? "" : "s"}
        </Typography>
      </Box>

      {error ? <Typography color="error">{error}</Typography> : null}

      {initialLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <InfiniteScroll
          dataLength={items.length}
          next={loadMore}
          hasMore={hasMore}
          loader={
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          }
          endMessage={
            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
              {items.length === 0
                ? "No wishlist items for this vendor."
                : "All wishlist items loaded."}
            </Typography>
          }
        >
          <Grid container spacing={2} sx={{ pb: 10 }}>
            {items.map((item) => {
              const product = item.product;
              const imageSrc = product?.featuredImage?.src;
              const minPrice =
                product?.priceRange?.minVariantPrice?.amount || "--";
              const maxPrice =
                product?.priceRange?.maxVariantPrice?.amount || "--";

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {imageSrc ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={imageSrc}
                        alt={product?.title || "Wishlist product"}
                        onClick={() => openLightbox(imageSrc, product?.title)}
                        sx={{
                          cursor: "pointer",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 180,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "grey.100",
                        }}
                      >
                        <Typography color="text.secondary">No image</Typography>
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {product?.title || "Product unavailable"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Price: ₹{minPrice}
                        {minPrice !== maxPrice ? ` - ₹${maxPrice}` : ""}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inventory: {product?.totalInventory ?? "--"}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
                        {item.source ? (
                          <Chip size="small" label={item.source} />
                        ) : null}
                        <Chip
                          size="small"
                          color="success"
                          label={item.status || "ACTIVE"}
                        />
                      </Stack>
                      {item.notes ? (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Notes: {item.notes}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary">
                        Added:{" "}
                        {item.createdAt ? formatDate(item.createdAt) : "--"}
                      </Typography>
                    </CardContent>
                    <CardActions
                      sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}
                    >
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => openDeleteConfirm(item)}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </InfiniteScroll>
      )}

      <Fab
        color="primary"
        variant="extended"
        onClick={openAddDialog}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1200,
        }}
      >
        <AddIcon sx={{ mr: 1 }} />
        Add product
      </Fab>

      <Dialog
        open={addDialogOpen}
        onClose={closeAddDialog}
        fullWidth
        maxWidth="md"
        aria-labelledby="wishlist-add-product-dialog-title"
        PaperProps={{
          sx: {
            minHeight: "75vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle id="wishlist-add-product-dialog-title">
          Add product to wishlist - {vendor?.name}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            minHeight: "60vh",
            overflow: "visible",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search and select a product to preview it before adding.
          </Typography>
          <Box sx={{ mb: 2, position: "relative", zIndex: 2 }}>
            <SearchAutoComplete
              getProductFromAutoComplete={handleProductSearchSelect}
              disablePortal={false}
              label="Search products"
            />
          </Box>

          {previewLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : null}

          {previewError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {previewError}
            </Alert>
          ) : null}

          {previewProduct ? (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  {previewImage ? (
                    <Box
                      component="img"
                      src={previewImage}
                      alt={previewProduct.title}
                      sx={{
                        width: "100%",
                        maxHeight: 240,
                        objectFit: "contain",
                        borderRadius: 1,
                        bgcolor: "grey.50",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.100",
                        borderRadius: 1,
                      }}
                    >
                      <Typography color="text.secondary">No image</Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Typography variant="h6" gutterBottom>
                    {previewProduct.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Price: ₹{previewMinPrice}
                    {previewMinPrice !== previewMaxPrice
                      ? ` - ₹${previewMaxPrice}`
                      : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inventory: {previewProduct.totalInventory ?? "--"}
                  </Typography>
                  {previewProduct.vendor ? (
                    <Typography variant="body2" color="text.secondary">
                      Vendor: {previewProduct.vendor}
                    </Typography>
                  ) : null}
                  {previewProduct.productType ? (
                    <Typography variant="body2" color="text.secondary">
                      Type: {previewProduct.productType}
                    </Typography>
                  ) : null}
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog} disabled={adding}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleAddToWishlist}
            disabled={!previewProduct || previewLoading || adding}
          >
            {adding ? "Adding..." : "Add to Wishlist"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDeleteConfirm}
        disableEscapeKeyDown={deleting}
        aria-labelledby="wishlist-delete-dialog-title"
        aria-describedby="wishlist-delete-dialog-description"
      >
        <DialogTitle id="wishlist-delete-dialog-title">
          Delete wishlist item - {vendor?.name}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="wishlist-delete-dialog-description">
            Are you sure you want to remove <strong>{deleteProductName}</strong>{" "}
            from this vendor wishlist? All customers for this vendor will no
            longer see this item.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
            autoFocus
          >
            {deleting ? "Deleting..." : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={lightbox.open}
        onClose={closeLightbox}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <Box sx={{ position: "relative", textAlign: "center" }}>
          <IconButton
            onClick={closeLightbox}
            aria-label="Close image"
            sx={{
              position: "absolute",
              top: -40,
              right: 0,
              color: "common.white",
              bgcolor: "rgba(0,0,0,0.5)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {lightbox.title ? (
            <Typography
              variant="subtitle1"
              sx={{ color: "common.white", mb: 1 }}
            >
              {lightbox.title}
            </Typography>
          ) : null}
          <Box
            component="img"
            src={lightbox.src}
            alt={lightbox.title || "Wishlist product"}
            sx={{
              maxWidth: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: 1,
            }}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}
